import { api } from '@/lib/api'
import type {
  AttachmentDownloadResponse,
  AttachmentPreviewTextResponse,
  CategoryCountsResponse,
  CategoryPayload,
  ClassificationMode,
  ConnectStartResponse,
  EstimateResponse,
  InboxAccount,
  InboxCategory,
  InboxEmail,
  InboxItem,
  InboxItemStatus,
  InboxWatchRule,
  OnboardingStartPayload,
  OnboardingState,
  SyncResult,
  WatchRulePayload,
} from './types'

type EstimatePayload = Pick<
  OnboardingStartPayload,
  'horizon' | 'custom_start' | 'custom_end' | 'scope' | 'source' | 'keywords'
>

export interface AutoSyncPayload {
  sync_interval_minutes: number
  auto_sync_enabled: boolean
  classification_mode?: ClassificationMode
}

export const inboxApi = {
  startConnect: () => api.get<ConnectStartResponse>('/api/inbox/connect/start'),
  listAccounts: () => api.get<{ accounts: InboxAccount[] }>('/api/inbox/accounts'),
  disconnect: (id: string) => api.del<{ ok: true }>(`/api/inbox/accounts/${id}`),
  sync: (id: string, maxResults?: number) =>
    api.post<SyncResult>(`/api/inbox/accounts/${id}/sync`, { maxResults }),
  updateAutoSync: (id: string, body: AutoSyncPayload) =>
    api.put<{ ok: true; account: Partial<InboxAccount> }>(`/api/inbox/accounts/${id}/autosync`, body),
  getOnboarding: (id: string) =>
    api.get<OnboardingState>(`/api/inbox/accounts/${id}/onboarding`),
  estimateOnboarding: (id: string, body: EstimatePayload) =>
    api.post<EstimateResponse>(`/api/inbox/accounts/${id}/onboarding/estimate`, body),
  startOnboarding: (id: string, body: OnboardingStartPayload) =>
    api.post<OnboardingState>(`/api/inbox/accounts/${id}/onboarding/start`, body),
  importOlder: (id: string) =>
    api.post<OnboardingState>(`/api/inbox/accounts/${id}/import-older`),
  listCategories: () => api.get<{ categories: InboxCategory[] }>('/api/inbox/categories'),
  createCategory: (body: CategoryPayload) =>
    api.post<{ category: InboxCategory }>('/api/inbox/categories', body),
  updateCategory: (id: string, body: CategoryPayload) =>
    api.put<{ category: InboxCategory }>(`/api/inbox/categories/${id}`, body),
  deleteCategory: (id: string) => api.del<{ ok: true }>(`/api/inbox/categories/${id}`),
  listRules: () => api.get<{ rules: InboxWatchRule[] }>('/api/inbox/rules'),
  createRule: (body: WatchRulePayload) =>
    api.post<{ rule: InboxWatchRule }>('/api/inbox/rules', body),
  updateRule: (id: string, body: WatchRulePayload) =>
    api.put<{ rule: InboxWatchRule }>(`/api/inbox/rules/${id}`, body),
  deleteRule: (id: string) => api.del<{ ok: true }>(`/api/inbox/rules/${id}`),
  createBillDraft: (itemId: string) =>
    api.post<{ ok: true; bill_id?: number; already?: boolean }>(
      `/api/inbox/items/${itemId}/create-bill-draft`
    ),
  categoryCounts: (accountId?: string) => {
    const qs = accountId ? `?account_id=${encodeURIComponent(accountId)}` : ''
    return api.get<CategoryCountsResponse>(`/api/inbox/category-counts${qs}`)
  },
  getEmail: (id: string) =>
    api.get<{ email: import('./types').InboxEmailDetail }>(`/api/inbox/emails/${id}`),
  listEmails: (params?: { accountId?: string; q?: string; categoryId?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.accountId) qs.set('account_id', params.accountId)
    if (params?.q) qs.set('q', params.q)
    if (params?.categoryId) qs.set('category_id', params.categoryId)
    if (params?.limit) qs.set('limit', String(params.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ emails: InboxEmail[] }>(`/api/inbox/emails${suffix}`)
  },
  listItems: (params?: { status?: InboxItemStatus; categoryId?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.categoryId) qs.set('category_id', params.categoryId)
    if (params?.limit) qs.set('limit', String(params.limit))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ items: InboxItem[] }>(`/api/inbox/items${suffix}`)
  },
  confirmItem: (id: string) => api.post<{ ok: true; item: InboxItem }>(`/api/inbox/items/${id}/confirm`),
  dismissItem: (id: string) => api.post<{ ok: true; item: InboxItem }>(`/api/inbox/items/${id}/dismiss`),
  reextractEmail: (id: string) =>
    api.post<{ ok: true; item: InboxItem }>(`/api/inbox/emails/${id}/extract`),
  attachmentDownload: (id: string) =>
    api.get<AttachmentDownloadResponse>(`/api/inbox/attachments/${id}/download`),
  attachmentPreviewText: (id: string) =>
    api.get<AttachmentPreviewTextResponse>(`/api/inbox/attachments/${id}/preview-text`),
}
